"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APP_PAGE_REPORT_PREVIEW_MESSAGE = exports.APP_PAGE_REPORT_MAX_TASKS = exports.APP_PAGE_REPORT_MAX_BYTES = exports.isAppColumnsQuery = exports.APP_WORKSTATE_WRITE_PARTS = exports.APP_WRITE_STEP_PARTS = exports.APP_WRITER_WORK = exports.APP_WRITER_FOLDS = exports.APP_WRITER_READS = exports.APP_WRITER_STAGES = exports.APP_WRITE_STAGES = exports.APP_FINDING_KINDS = void 0;
exports.createUsageCounters = createUsageCounters;
exports.readAppPageReport = readAppPageReport;
/**
 * What the pages and Agent Actions of an App cost, counted per place (a
 * route, an Agent Action or an App file), and the patterns in these counts that point at
 * needless work. Only counters: a host notes what it answers and writes
 * anyway, the page report (app-runtime/page-report.ts) what the page
 * rendered, loaded and the long tasks the browser saw. No profiler, no trace.
 *
 * The one logic of the findings for every host: the StreamKit server counts
 * reads, writes and page reports; the App Studio counts the page reports of
 * its preview. A finding names its kind, its place, the site in the code when
 * it is known and the values of its texts
 * `com.streamlain.streamkit.findings.<kind>.cause|remedy` of the platform
 * catalog.
 */
const app_base_binding_read_1 = require("./app-base-binding-read");
const app_base_bridge_budgets_1 = require("./app-base-bridge-budgets");
const app_base_contract_1 = require("./app-base-contract");
const app_limits_1 = require("./app-limits");
exports.APP_FINDING_KINDS = [
    'fullRead',
    'repeatRead',
    'largeAnswer',
    'slowScript',
    'writeStorm',
    'pageLoad',
    'pageRequests',
    'idChunks',
    'columnsQuery',
    'ownStamp',
];
/** The stages of a write, in their order. */
exports.APP_WRITE_STAGES = [
    'queue',
    'read',
    'run',
    'complete',
    'write',
];
/**
 * The parts of the `write` stage the row writer of the working state spends:
 * reading the documents, planning the rows without the size encodes, the
 * full encodes that measure shard sizes exactly and committing.
 */
exports.APP_WRITER_STAGES = [
    'read',
    'compute',
    'encode',
    'commit',
];
/**
 * The documents the row writer of the working state read for its writes:
 * from their stored base, and continued from the documents it kept open
 * since its last write; and why it read a stored base: never written by
 * this writer, let go (more documents than it keeps) or kept at a head that
 * moved.
 */
exports.APP_WRITER_READS = [
    'storedBaseReads',
    'keptDocuments',
    'firstReads',
    'limitReads',
    'movedHeadReads',
];
/**
 * The logs of the documents the row writer looked at for folding after the
 * writes, those it folded into their snapshot and the updates and bytes
 * these held.
 */
exports.APP_WRITER_FOLDS = [
    'logsChecked',
    'logsFolded',
    'updatesFolded',
    'bytesFolded',
];
/**
 * The work the row writer counted while it planned the writes, beside the
 * documents it read: rows and cells it took in and materialized, shards it
 * materialized whole, measured or encoded in full, and the root, manifest
 * and directory it read and wrote.
 */
exports.APP_WRITER_WORK = [
    'inputRows',
    'inputCells',
    'materializedCells',
    'fullShardMaterializations',
    'sizingProbes',
    'reusedShardCandidates',
    'rootFieldsRead',
    'manifestReads',
    'fullEncodes',
    'fullEncodeBytes',
    'directoryShards',
    'directoryPartsRead',
    'directoryEntriesRead',
    'directoryPartsWritten',
];
/**
 * The parts of the `write` stage, which add up to it: holding the copies of
 * the files the rows name, the host writing the start data, the row writer
 * (the sum of APP_WRITER_STAGES), planning the changes of a write, keeping
 * what landed (rows and shards), writing the records of the shards, folding
 * the logs of the documents written, naming the uploads the cells hold, the
 * record of the working state and the checks of its tables,
 * the Apps hearing the write, and what the write stage spent besides.
 */
exports.APP_WRITE_STEP_PARTS = [
    'files',
    'seed',
    'writer',
    'plan',
    'keep',
    'shardRecords',
    'compact',
    'uploads',
    'record',
    'hear',
    'other',
];
/** The parts of the write stage the working state of the StreamKit spends. */
exports.APP_WORKSTATE_WRITE_PARTS = [
    'plan',
    'keep',
    'shardRecords',
    'compact',
    'uploads',
    'record',
];
/**
 * A read of more rows than one bound list reads at a time that answers the
 * whole table reads more than a page shows at once.
 */
const FULL_READ_MIN_ROWS = app_base_binding_read_1.APP_BASE_BINDING_WINDOW_ROWS;
/** An answer larger than one transport window comes in several requests. */
const LARGE_ANSWER_BYTES = app_base_contract_1.APP_BASE_BUDGETS.maxDatasetChunkBytes;
/**
 * A page that loads near the reads its Bridge keeps (heldReadBytes) loads
 * more than it can hold to read only what changed; the share warns as the
 * bar warns of every limit.
 */
const PAGE_LOAD_BYTES = app_limits_1.APP_LIMIT_WARN_SHARE * app_base_bridge_budgets_1.APP_BASE_BRIDGE_BUDGETS.heldReadBytes;
/**
 * A visit that asks eight rounds of the reads a Bridge runs at once: over
 * the internet each round costs a wait.
 */
const PAGE_REQUESTS = 8 * app_base_bridge_budgets_1.APP_BASE_BRIDGE_BUDGETS.parallelReads;
const MB = 1024 * 1024;
// Regression guards, not limits of the platform: the same question answered
// in full three times on one state, a task that holds the page twice as long
// as a frame budget of 100 ms allows, ten writes of one place within ten
// seconds.
const REPEAT_READ_MIN = 3;
/**
 * The questions a route keeps: a question names the state of the rows it
 * read, so one on rows that changed since is a new question; the oldest go.
 */
const QUESTIONS_KEPT = 256;
const SLOW_TASK_MS = 200;
const WRITE_STORM_WRITES = 10;
const WRITE_STORM_WINDOW_MS = 10_000;
/**
 * The same question on as many different `rowIds` within as many seconds as
 * a write storm reads one relation in parts; a detail page that reads its
 * one row per visit asks them far apart.
 */
const ID_CHUNKS_MIN = 3;
/**
 * A read of at most one row asks a source for its columns when the page
 * draws none of it: `describe().sources` names them without a read.
 */
const COLUMNS_QUERY_MAX_ROWS = 1;
/** Whether a query asks for no row (`rowIds: []`) or at most one (`limit`). */
const isAppColumnsQuery = ({ rowIds, limit, }) => (Array.isArray(rowIds) && rowIds.length === 0) ||
    (typeof limit === 'number' && limit <= COLUMNS_QUERY_MAX_ROWS);
exports.isAppColumnsQuery = isAppColumnsQuery;
/**
 * The longest report a page sends: its route, rendered rows, long tasks,
 * loaded bytes, requests and widest read.
 */
exports.APP_PAGE_REPORT_MAX_BYTES = 16 * 1024;
/** The long tasks one report carries at most; the page report stops there. */
exports.APP_PAGE_REPORT_MAX_TASKS = 64;
/** The message that brings a page report of the preview to the App Studio. */
exports.APP_PAGE_REPORT_PREVIEW_MESSAGE = 'streamlain:app-page-report';
const placeKey = (place) => `${place.kind}:${place.id}`;
const emptyCounts = (place) => ({
    place,
    queries: 0,
    rowsRead: 0,
    bytesRead: 0,
    writes: 0,
    rowsWritten: 0,
    bytesWritten: 0,
    repeats: 0,
    answerMs: 0,
    slowestMs: 0,
    // The wall time of the writes by stage: waiting for the actions and saves
    // in line before, reading the rows, running the action, the changes the
    // host completes and writing them. `answerMs` is the sum of all but the
    // wait. A refused write writes nothing and counts in no stage.
    stageMs: Object.fromEntries(exports.APP_WRITE_STAGES.map(stage => [stage, 0])),
    writerMs: Object.fromEntries(exports.APP_WRITER_STAGES.map(stage => [stage, 0])),
    writeMs: Object.fromEntries(exports.APP_WRITE_STEP_PARTS.map(part => [part, 0])),
    writerReads: Object.fromEntries(exports.APP_WRITER_READS.map(read => [read, 0])),
    writerFolds: Object.fromEntries(exports.APP_WRITER_FOLDS.map(fold => [fold, 0])),
    writerWork: Object.fromEntries(exports.APP_WRITER_WORK.map(work => [work, 0])),
    longTasks: 0,
    longestTaskMs: 0,
});
/**
 * The counters of one host. `now()` gives the time of a write in ms, so
 * that a spec can set it.
 */
function createUsageCounters({ now = () => performance.now(), } = {}) {
    const places = new Map();
    // Per place, the questions answered on a state and how often.
    const answered = new Map();
    const writeTimes = new Map();
    // Per route, the widest read of a whole table, a table answered in more
    // than one window and the most rows its page rendered.
    const wholeReads = new Map();
    const largest = new Map();
    const rendered = new Map();
    // Per route, the most bytes one visit of its page loaded, as the page
    // reported them.
    const loads = new Map();
    const storms = new Map();
    const slowest = new Map();
    // Per route, the most requests one visit of its page made; per question
    // without its `rowIds`, the row sets of the last window with the time each
    // was asked; per App file, an address of it with a version never given.
    const requests = new Map();
    const chunks = new Map();
    const stamps = new Set();
    // Per route, the first source asked for its columns only and whether its
    // page reported at all.
    const heads = new Map();
    const reported = new Set();
    const countsOf = (place) => {
        const key = placeKey(place);
        let counts = places.get(key);
        if (!counts)
            places.set(key, (counts = emptyCounts(place)));
        return counts;
    };
    const noteWholeRead = (key, sourceKey, rows) => {
        if (rows > FULL_READ_MIN_ROWS && rows > (wholeReads.get(key)?.rows ?? 0))
            wholeReads.set(key, { sourceKey, rows });
    };
    return {
        noteRead({ route, sourceKey, question, rows, totalRows, bytes, split, ms, idChunk, columnsOnly, }) {
            const place = { kind: 'route', id: route };
            const counts = countsOf(place);
            counts.queries += 1;
            counts.rowsRead += rows;
            counts.bytesRead += bytes;
            counts.answerMs += ms;
            counts.slowestMs = Math.max(counts.slowestMs, ms);
            const key = placeKey(place);
            let questions = answered.get(key);
            if (!questions)
                answered.set(key, (questions = new Map()));
            const times = (questions.get(question)?.times ?? 0) + 1;
            // Asked again, it becomes the newest of the questions kept.
            questions.delete(question);
            questions.set(question, { sourceKey, times });
            if (questions.size > QUESTIONS_KEPT)
                questions.delete(questions.keys().next().value);
            if (times > 1)
                counts.repeats += 1;
            if (rows === totalRows)
                noteWholeRead(key, sourceKey, rows);
            if (split || bytes >= LARGE_ANSWER_BYTES)
                largest.set(key, { sourceKey });
            if (columnsOnly && !heads.has(key))
                heads.set(key, { sourceKey });
            if (idChunk) {
                let asked = chunks.get(key);
                if (!asked)
                    chunks.set(key, (asked = new Map()));
                let sets = asked.get(idChunk.question);
                if (!sets) {
                    if (asked.size >= QUESTIONS_KEPT)
                        asked.delete(asked.keys().next().value);
                    asked.set(idChunk.question, (sets = { sourceKey, rows: new Map(), parted: false }));
                }
                const at = now();
                sets.rows.set(idChunk.rows, at);
                for (const [rows, time] of sets.rows)
                    if (at - time >= WRITE_STORM_WINDOW_MS)
                        sets.rows.delete(rows);
                if (sets.rows.size >= ID_CHUNKS_MIN)
                    sets.parted = true;
            }
        },
        noteWrite({ place, rows, bytes, ms = 0, stages = {}, writer = {}, writeParts = {}, writerReads = {}, writerFolds = {}, writerWork = {}, }) {
            const counts = countsOf(place);
            counts.writes += 1;
            counts.rowsWritten += rows;
            if (bytes !== undefined)
                counts.bytesWritten += bytes;
            counts.answerMs += ms;
            counts.slowestMs = Math.max(counts.slowestMs, ms);
            for (const stage of exports.APP_WRITE_STAGES)
                counts.stageMs[stage] += stages[stage] ?? 0;
            for (const stage of exports.APP_WRITER_STAGES)
                counts.writerMs[stage] += writer[stage] ?? 0;
            for (const part of exports.APP_WRITE_STEP_PARTS)
                counts.writeMs[part] += writeParts[part] ?? 0;
            for (const read of exports.APP_WRITER_READS)
                counts.writerReads[read] += writerReads[read] ?? 0;
            for (const fold of exports.APP_WRITER_FOLDS)
                counts.writerFolds[fold] += writerFolds[fold] ?? 0;
            for (const work of exports.APP_WRITER_WORK)
                counts.writerWork[work] += writerWork[work] ?? 0;
            const key = placeKey(place);
            const at = now();
            const times = [...(writeTimes.get(key) ?? []), at].filter(time => at - time < WRITE_STORM_WINDOW_MS);
            writeTimes.set(key, times);
            if (times.length >= WRITE_STORM_WRITES)
                storms.set(key, Math.max(storms.get(key) ?? 0, times.length));
        },
        /**
         * What the page of `route` reported: the rows its bindings rendered, the
         * long tasks the browser saw, each with the script it ran in when the
         * browser names it, the bytes its reads loaded since the page came to
         * the route, the requests they made and its widest read. A widest read of the whole table is a
         * whole read as a host that answers it counts one.
         */
        notePage({ route, renderedRows, longTasks = [], loadedBytes, requests: made, widestRead, }) {
            const place = { kind: 'route', id: route };
            const counts = countsOf(place);
            const key = placeKey(place);
            reported.add(key);
            if (typeof renderedRows === 'number')
                rendered.set(key, Math.max(rendered.get(key) ?? 0, renderedRows));
            if (typeof loadedBytes === 'number')
                loads.set(key, Math.max(loads.get(key) ?? 0, loadedBytes));
            if (typeof made === 'number')
                requests.set(key, Math.max(requests.get(key) ?? 0, made));
            if (widestRead && widestRead.rows === widestRead.total)
                noteWholeRead(key, widestRead.sourceKey, widestRead.rows);
            for (const task of longTasks) {
                counts.longTasks += 1;
                counts.longestTaskMs = Math.max(counts.longestTaskMs, task.ms);
                if (task.ms >= SLOW_TASK_MS && task.ms > (slowest.get(key)?.ms ?? 0))
                    slowest.set(key, task);
            }
        },
        noteStamp({ file }) {
            const place = { kind: 'file', id: file };
            countsOf(place);
            stamps.add(placeKey(place));
        },
        /** The counts of every place and the patterns found in them. */
        read() {
            const findings = [];
            for (const [key, counts] of places) {
                const { place } = counts;
                const whole = wholeReads.get(key);
                const shown = rendered.get(key);
                // Only a page that reported what it rendered shows less; one without
                // bindings reports nothing, and nothing is claimed for it.
                if (whole && shown !== undefined && shown < whole.rows)
                    findings.push({
                        kind: 'fullRead',
                        place,
                        values: { source: whole.sourceKey, rows: whole.rows, shown },
                    });
                const repeated = [...(answered.get(key) ?? [])].find(([, { times }]) => times >= REPEAT_READ_MIN);
                if (repeated)
                    findings.push({
                        kind: 'repeatRead',
                        place,
                        values: { source: repeated[1].sourceKey, times: repeated[1].times },
                    });
                const large = largest.get(key);
                if (large)
                    findings.push({
                        kind: 'largeAnswer',
                        place,
                        values: { source: large.sourceKey, limit: LARGE_ANSWER_BYTES },
                    });
                const slow = slowest.get(key);
                if (slow)
                    findings.push({
                        kind: 'slowScript',
                        place,
                        ...(slow.site ? { site: slow.site } : {}),
                        values: { ms: Math.round(slow.ms), tasks: counts.longTasks },
                    });
                const loaded = loads.get(key) ?? 0;
                if (loaded >= PAGE_LOAD_BYTES)
                    findings.push({
                        kind: 'pageLoad',
                        place,
                        values: {
                            mb: (loaded / MB).toFixed(1),
                            limit: (PAGE_LOAD_BYTES / MB).toFixed(1),
                        },
                    });
                const storm = storms.get(key);
                if (storm !== undefined)
                    findings.push({
                        kind: 'writeStorm',
                        place,
                        values: { writes: storm, seconds: WRITE_STORM_WINDOW_MS / 1000 },
                    });
                const made = requests.get(key) ?? 0;
                if (made >= PAGE_REQUESTS)
                    findings.push({
                        kind: 'pageRequests',
                        place,
                        values: { requests: made, limit: PAGE_REQUESTS },
                    });
                const parted = [...(chunks.get(key)?.values() ?? [])].find(({ parted }) => parted);
                if (parted)
                    findings.push({
                        kind: 'idChunks',
                        place,
                        values: { source: parted.sourceKey, times: ID_CHUNKS_MIN },
                    });
                // A page that reported and drew no row read the columns only; a
                // page that never reported is claimed nothing.
                const head = heads.get(key);
                if (head && reported.has(key) && !shown)
                    findings.push({
                        kind: 'columnsQuery',
                        place,
                        values: { source: head.sourceKey },
                    });
                if (stamps.has(key))
                    findings.push({
                        kind: 'ownStamp',
                        place,
                        values: { file: place.id },
                    });
            }
            return {
                places: [...places.values()].map(counts => ({
                    ...counts,
                    stageMs: { ...counts.stageMs },
                    writerMs: { ...counts.writerMs },
                    writeMs: { ...counts.writeMs },
                    writerReads: { ...counts.writerReads },
                    writerFolds: { ...counts.writerFolds },
                    writerWork: { ...counts.writerWork },
                })),
                findings,
            };
        },
    };
}
const isText = (value, max) => typeof value === 'string' && value.length > 0 && value.length <= max;
const isCount = (value) => Number.isSafeInteger(value) && value >= 0;
const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
/**
 * What a page reports, checked: `{ route, renderedRows?, longTasks?,
 * loadedBytes?, requests?, widestRead? }`, each long task `{ ms, site? }`, the widest
 * read `{ sourceKey, rows, total }`. Returns null for anything else.
 */
function readAppPageReport(value) {
    if (!isRecord(value))
        return null;
    const { route, renderedRows, longTasks = [], loadedBytes, requests, widestRead, ...rest } = value;
    if (Object.keys(rest).length ||
        (loadedBytes !== undefined && !isCount(loadedBytes)) ||
        (requests !== undefined && !isCount(requests)) ||
        (widestRead !== undefined &&
            (!isRecord(widestRead) ||
                Object.keys(widestRead).some(field => field !== 'sourceKey' && field !== 'rows' && field !== 'total') ||
                !isText(widestRead.sourceKey, 512) ||
                !isCount(widestRead.rows) ||
                !isCount(widestRead.total) ||
                widestRead.total < widestRead.rows)) ||
        !isText(route, 512) ||
        !route.startsWith('/') ||
        (renderedRows !== undefined && !isCount(renderedRows)) ||
        !Array.isArray(longTasks) ||
        longTasks.length > exports.APP_PAGE_REPORT_MAX_TASKS)
        return null;
    const tasks = [];
    for (const task of longTasks) {
        if (!isRecord(task))
            return null;
        const { ms, site, ...more } = task;
        if (Object.keys(more).length ||
            !(typeof ms === 'number' && Number.isFinite(ms) && ms >= 0) ||
            (site !== undefined && !isText(site, 512)))
            return null;
        tasks.push(site === undefined ? { ms } : { ms, site });
    }
    const widestReadChecked = widestRead;
    return {
        route,
        ...(renderedRows === undefined ? {} : { renderedRows }),
        longTasks: tasks,
        ...(loadedBytes === undefined ? {} : { loadedBytes }),
        ...(requests === undefined ? {} : { requests: requests }),
        ...(widestReadChecked === undefined
            ? {}
            : {
                widestRead: {
                    sourceKey: widestReadChecked.sourceKey,
                    rows: widestReadChecked.rows,
                    total: widestReadChecked.total,
                },
            }),
    };
}
